/**
 * MSAL authentication configuration for the standalone SPA.
 *
 * Based on the existing src/auth.js from the monorepo,
 * but simplified to MSAL-only (ADAL will be dropped in Phase 2.5).
 */
import { PublicClientApplication } from '@azure/msal-browser';
import { clearLocalAuthAndLogin as recoverLocalAuthAndLogin } from './authRecovery';

const TENANT = '1179056b-9a39-4611-9e0f-77943caae68f';
const CLIENT_ID = '09453464-f731-4018-bd0c-c049f6220bee';

const runtimeOrigin = typeof window !== 'undefined' ? `${window.location.origin}/` : 'http://localhost:45655/';
const AZURE_BASE_URL = import.meta.env.VITE_AZURE_BASE_URL || runtimeOrigin;

const msalConfig = {
    auth: {
        clientId: CLIENT_ID,
        authority: `https://login.microsoftonline.com/${TENANT}`,
        redirectUri: new URL('authentication-response', AZURE_BASE_URL).toString(),
        postLogoutRedirectUri: AZURE_BASE_URL,
    },
    cache: {
        cacheLocation: 'localStorage',
    },
};

export const loginRequest = {
    scopes: ['openid', 'profile', 'email'],
};

let msalInstance = null;
let fakeEmail = null;

export const setFakeEmail = (email) => {
    fakeEmail = email;
};

export const getMsalInstance = async () => {
    if (!msalInstance) {
        msalInstance = new PublicClientApplication(msalConfig);
        await msalInstance.initialize();
    }
    return msalInstance;
};

export const getActiveAccount = () => {
    if (!msalInstance) return null;
    return msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null;
};

export const getCurrentUserEmail = () => {
    if (fakeEmail) return fakeEmail;
    const account = getActiveAccount();
    if (!account) return null;
    return (
        account.idTokenClaims?.preferred_username ||
        account.idTokenClaims?.email ||
        account.username ||
        null
    );
};

export const getUserProfileFromIdentity = (account) => {
    if (!account) return {};
    const claims = account.idTokenClaims || {};
    return {
        firstName: claims.given_name || '',
        lastName: claims.family_name || '',
        email: claims.preferred_username || claims.email || account.username || '',
    };
};

export const logoutCurrentUser = async () => {
    if (msalInstance) {
        await msalInstance.logoutRedirect();
    }
};

/**
 * Local logout (clear MSAL cache) then start a fresh interactive login.
 * Use after a failed authentication so the user can sign in again in one step.
 */
export const clearLocalAuthAndLogin = async (options = {}) => {
    const instance = await getMsalInstance();
    return recoverLocalAuthAndLogin(instance, loginRequest, options);
};
